import React from 'react';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    signInWithEmailAndPassword,
    onAuthStateChanged,
    signOut
} from 'firebase/auth';
import {
    getFirestore,
    collection,
    query,
    where,
    onSnapshot,
    doc,
    updateDoc,
    setDoc,
    Timestamp
} from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// --- Firebase Configuration ---
// VLOŽTE ZDE NOVOU KONFIGURACI Z PROJEKTU 'Vysledkove protokoly'
const firebaseConfig = {
    apiKey: "AIzaSyAC1m-TfUWObIojoLijeB9tvjnHl-oWsNo",
    authDomain: "vysledkove-protokoly.firebaseapp.com",
    projectId: "vysledkove-protokoly",
    storageBucket: "vysledkove-protokoly.firebaseapp.com",
    messagingSenderId: "347054881084",
    appId: "1:347054881084:web:172b60ba9ba25ca6c68639"
};

// --- Initialize Firebase App ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);


// --- Constants ---
const ORDERS_COLLECTION = "objednavky_data";
const TARGET_EMAIL = "danum@danum.sk";
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1943'];
const ORDERS_PER_PAGE = 10;
const czechToSlovakMonths = {
    'leden': 'Január', 'únor': 'Február', 'březen': 'Marec', 'duben': 'Apríl',
    'květen': 'Máj', 'červen': 'Jún', 'červenec': 'Júl', 'srpen': 'August',
    'září': 'September', 'říjen': 'Október', 'listopad': 'November', 'prosinec': 'December'
};

// --- Helper Components ---

const StatCard = ({ title, value, icon, valueColor = 'text-gray-800' }) => (
    <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4 transition-transform transform hover:scale-105">
        <div className="bg-blue-100 text-blue-600 p-3 rounded-full">
            {icon}
        </div>
        <div>
            <p className="text-gray-500 text-sm font-medium">{title}</p>
            <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
        </div>
    </div>
);

const ChartContainer = ({ title, children }) => (
    <div className="bg-white p-6 rounded-xl shadow-lg mt-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{title}</h3>
        <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>{children}</ResponsiveContainer>
        </div>
    </div>
);


// --- Main App Component ---
function App() {
    const [user, setUser] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState('');

    const [allTimeOrders, setAllTimeOrders] = React.useState([]);
    const [selectedDate, setSelectedDate] = React.useState(new Date());

    const [currentPage, setCurrentPage] = React.useState(1);


    // --- Authentication Effect ---
    React.useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // --- Data Fetching Effect ---
    React.useEffect(() => {
        if (!user) {
            setAllTimeOrders([]);
            return;
        };

        const q = query(
            collection(db, ORDERS_COLLECTION),
            where("email", "==", TARGET_EMAIL)
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedOrders = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const dateString = data.datumObjednavky;
                if (dateString && typeof dateString === 'string') {
                    const parsedDate = new Date(dateString);
                    if (!isNaN(parsedDate.getTime())) {
                        fetchedOrders.push({
                            id: doc.id,
                            ...data,
                            processingDate: parsedDate,
                        });
                    }
                }
            });
            setAllTimeOrders(fetchedOrders);
        }, (err) => {
            console.error("Error fetching orders: ", err);
            setError("Nepodařilo se načíst data objednávek.");
        });

        return () => unsubscribe();
    }, [user]);
    
    // --- Event Handlers ---
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            setError('Neplatné přihlašovací údaje. Zkuste to prosím znovu.');
            console.error("Login error:", err);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (err) {
            console.error("Logout error:", err);
        }
    };
    
    const handleCheckboxChange = async (orderId, currentStatus) => {
        const orderRef = doc(db, ORDERS_COLLECTION, orderId);
        try {
            await updateDoc(orderRef, {
                danumOdsouhlaseno: !currentStatus
            });
        } catch (err) {
            console.error("Error updating order status: ", err);
            setError("Chyba při aktualizaci stavu objednávky.");
        }
    };

    const changeMonth = (offset) => {
        setSelectedDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setMonth(newDate.getMonth() + offset);
            return newDate;
        });
        setCurrentPage(1); // Reset to first page on month change
    };

    // --- Data Processing & Memoization ---
    const filteredOrders = React.useMemo(() => {
        return allTimeOrders.filter(order =>
            order.processingDate.getMonth() === selectedDate.getMonth() &&
            order.processingDate.getFullYear() === selectedDate.getFullYear()
        );
    }, [allTimeOrders, selectedDate]);

    const stats = React.useMemo(() => {
        const approvedCount = filteredOrders.filter(o => o.danumOdsouhlaseno).length;
        const totalValue = filteredOrders.reduce((acc, order) => acc + (order.totalPriceWithoutVat || 0), 0);
        const approvedValue = filteredOrders
            .filter(o => o.danumOdsouhlaseno)
            .reduce((acc, order) => acc + (order.totalPriceWithoutVat || 0), 0);
        
        const productCounts = filteredOrders.reduce((acc, order) => {
            if (order.polozkyProdukty && Array.isArray(order.polozkyProdukty)) {
                order.polozkyProdukty.forEach(product => {
                    if(product.nazev) {
                        acc[product.nazev] = (acc[product.nazev] || 0) + 1;
                    }
                });
            }
            return acc;
        }, {});

        const productPieData = Object.entries(productCounts).map(([name, value]) => ({ name, value }));
        
        return {
            totalSamples: filteredOrders.length,
            approvedCount,
            progress: filteredOrders.length > 0 ? (approvedCount / filteredOrders.length) * 100 : 0,
            totalValue,
            approvedValue,
            productPieData,
        };
    }, [filteredOrders]);
    
    const yearOverYearStats = React.useMemo(() => {
        const currentYear = selectedDate.getFullYear();
        const selectedMonth = selectedDate.getMonth();

        const getMonthStats = (month, year) => {
            const monthOrders = allTimeOrders.filter(o => 
                o.processingDate.getMonth() === month && o.processingDate.getFullYear() === year
            );
            return {
                samples: monthOrders.length,
                turnover: monthOrders.reduce((sum, o) => sum + (o.totalPriceWithoutVat || 0), 0)
            };
        };

        const currentMonthStats = getMonthStats(selectedMonth, currentYear);
        const lastYearMonthStats = getMonthStats(selectedMonth, currentYear - 1);

        let percentageChange = null;
        if (lastYearMonthStats.samples > 0) {
            percentageChange = ((currentMonthStats.samples - lastYearMonthStats.samples) / lastYearMonthStats.samples) * 100;
        } else if (currentMonthStats.samples > 0) {
            percentageChange = 100; 
        }

        const currentYTDOrders = allTimeOrders.filter(o => 
            o.processingDate.getFullYear() === currentYear &&
            o.processingDate.getMonth() <= selectedMonth
        );
        
        const lastYTDOrders = allTimeOrders.filter(o =>
            o.processingDate.getFullYear() === (currentYear - 1) &&
            o.processingDate.getMonth() <= selectedMonth
        );
        
        let ytdPercentageChange = null;
        if (lastYTDOrders.length > 0) {
            ytdPercentageChange = ((currentYTDOrders.length - lastYTDOrders.length) / lastYTDOrders.length) * 100;
        } else if (currentYTDOrders.length > 0) {
            ytdPercentageChange = 100;
        }

        return {
            percentageChange: percentageChange !== null ? percentageChange.toFixed(1) : null,
            currentYTDCount: currentYTDOrders.length,
            lastYTDCount: lastYTDOrders.length,
            ytdPercentageChange: ytdPercentageChange !== null ? ytdPercentageChange.toFixed(1) : null,
        };
    }, [allTimeOrders, selectedDate]);

     const comparisonChartData = React.useMemo(() => {
        const getMonthStats = (date) => {
            const monthOrders = allTimeOrders.filter(o => 
                o.processingDate.getMonth() === date.getMonth() && o.processingDate.getFullYear() === date.getFullYear()
            );
            return {
                samples: monthOrders.length,
                turnover: monthOrders.reduce((sum, o) => sum + (o.totalPriceWithoutVat || 0), 0)
            };
        };
        
        const previousMonthDate = new Date(selectedDate);
        previousMonthDate.setMonth(previousMonthDate.getMonth() - 1);
        
        const lastYearDate = new Date(selectedDate);
        lastYearDate.setFullYear(lastYearDate.getFullYear() - 1);

        const currentMonthStats = getMonthStats(selectedDate);
        const prevMonthStats = getMonthStats(previousMonthDate);
        const lastYearMonthStats = getMonthStats(lastYearDate);
        
        return [
            { name: 'Minulý rok (stejný měsíc)', 'Počet vzorků': lastYearMonthStats.samples, 'Obrat': lastYearMonthStats.turnover },
            { name: 'Minulý měsíc', 'Počet vzorků': prevMonthStats.samples, 'Obrat': prevMonthStats.turnover },
            { name: 'Aktuální měsíc', 'Počet vzorků': currentMonthStats.samples, 'Obrat': currentMonthStats.turnover },
        ];
    }, [allTimeOrders, selectedDate]);

    // --- Pagination Logic ---
    const sortedOrders = React.useMemo(() => 
        [...filteredOrders].sort((a, b) => b.processingDate - a.processingDate),
        [filteredOrders]
    );

    const totalPages = Math.ceil(sortedOrders.length / ORDERS_PER_PAGE);
    const indexOfLastOrder = currentPage * ORDERS_PER_PAGE;
    const indexOfFirstOrder = indexOfLastOrder - ORDERS_PER_PAGE;
    const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);


    // --- Render Logic ---
    if (isLoading) {
        return <div className="flex items-center justify-center min-h-screen bg-gray-100"><div className="text-xl font-semibold">Načítání...</div></div>;
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center">
                <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-xl">
                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Backoffice VHS</h2>
                    <p className="text-center text-gray-500 mb-8">Přihlaste se pro pokračování</p>
                    <form onSubmit={handleLogin}>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                                Email
                            </label>
                            <input
                                className="shadow-sm appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                id="email"
                                type="email"
                                placeholder="vas.email@domena.cz"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                                Heslo
                            </label>
                            <input
                                className="shadow-sm appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                id="password"
                                type="password"
                                placeholder="******************"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        {error && <p className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 text-center">{error}</p>}
                        <div className="flex items-center justify-between">
                            <button
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-colors"
                                type="submit">
                                Přihlásit se
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
    
    // Logic for month name display
    const czechMonth = selectedDate.toLocaleString('cs-CZ', { month: 'long' });
    const capitalizedCzechMonth = czechMonth.charAt(0).toUpperCase() + czechMonth.slice(1);
    const slovakMonth = czechToSlovakMonths[czechMonth.toLowerCase()];
    const year = selectedDate.getFullYear();
    const displayDate = `${capitalizedCzechMonth} (${slovakMonth}) ${year}`;

    const ArrowIcon = ({ value }) => {
        if (value === null) return <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" /></svg>;
        const color = value > 0 ? "text-green-500" : "text-red-500";
        const path = value > 0 
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m0 0l-7-7m7 7l7-7" />;
        return <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 ${color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">{path}</svg>;
    };

    // --- Logged-in Dashboard View ---
    return (
        <div className="min-h-screen bg-gray-50 text-gray-800">
            <header className="bg-white shadow-md p-4 flex justify-between items-center">
                <h1 className="text-2xl font-bold text-black-600">Rozbory vody - Danum</h1>
                <div className="flex items-center">
                    <span className="mr-4 text-gray-600">{user.email}</span>
                    <button onClick={handleLogout} className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors">
                        Odhlásit se
                    </button>
                </div>
            </header>

            <main className="p-8">
                {/* Month Selector */}
                <div className="flex justify-between items-center mb-6 bg-white p-4 rounded-xl shadow-lg">
                     <button onClick={() => changeMonth(-1)} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        &larr; Předchozí
                    </button>
                    <h2 className="text-2xl font-bold text-center">
                        {displayDate}
                    </h2>
                    <button onClick={() => changeMonth(1)} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        Následující &rarr;
                    </button>
                </div>

                {/* KPI Cards */}
                 <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-6">
                    <StatCard title="Celkem přijatých vzorků" value={stats.totalSamples} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>} />
                    <StatCard title="Celkový obrat (bez DPH)" value={`${stats.totalValue.toLocaleString('cs-CZ')} CZK`} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>} />
                    <StatCard 
                        title="Meziroční srovnání (měsíc)" 
                        value={yearOverYearStats.percentageChange !== null ? `${yearOverYearStats.percentageChange > 0 ? '+' : ''}${yearOverYearStats.percentageChange}%` : 'N/A'}
                        valueColor={yearOverYearStats.percentageChange > 0 ? 'text-green-500' : 'text-red-500'}
                        icon={<ArrowIcon value={yearOverYearStats.percentageChange} />} 
                    />
                    <StatCard 
                        title="Výkon YTD (vs. loni)" 
                        value={
                           <div>
                                <span>{`${yearOverYearStats.currentYTDCount} / ${yearOverYearStats.lastYTDCount}`}</span>
                                {yearOverYearStats.ytdPercentageChange !== null && (
                                    <div className={`font-bold text-base mt-1 ${yearOverYearStats.ytdPercentageChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                        ({yearOverYearStats.ytdPercentageChange > 0 ? '+' : ''}{yearOverYearStats.ytdPercentageChange}%)
                                    </div>
                                )}
                           </div>
                        }
                        icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
                    />
                    <StatCard title="K fakturaci" value={`${stats.approvedValue.toLocaleString('cs-CZ')} CZK`} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>} />
                </div>
                
                {/* Progress Bar */}
                <div className="bg-white p-4 rounded-xl shadow-lg mb-8">
                     <h3 className="text-lg font-semibold mb-2">Stav odsouhlasených objednávek</h3>
                     <div className="w-full bg-gray-200 rounded-full h-4">
                        <div className="bg-green-500 h-4 rounded-full" style={{ width: `${stats.progress}%` }}></div>
                     </div>
                     <p className="text-right text-sm text-gray-600 mt-1">{stats.progress.toFixed(1)}% hotovo</p>
                </div>


                {/* Orders Table */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-800">Přehled objednávek</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-max text-left">
                            <thead className="border-b bg-gray-50">
                                <tr>
                                    <th className="p-4 font-semibold">Datum zpracování</th>
                                    <th className="p-4 font-semibold">Číslo objednávky</th>
                                    <th className="p-4 font-semibold">Jméno zákazníka</th>
                                    <th className="p-4 font-semibold">Produkty</th>
                                    <th className="p-4 font-semibold text-right">Cena (bez DPH)</th>
                                    <th className="p-4 font-semibold">Odsouhlaseno</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center p-8 text-gray-500">Pro vybraný měsíc nebyly nalezeny žádné objednávky.</td>
                                    </tr>
                                ) : (
                                    currentOrders.map(order => (
                                        <tr key={order.id} className="border-b hover:bg-gray-50">
                                            <td className="p-4">{order.processingDate.toLocaleDateString('cs-CZ')}</td>
                                            <td className="p-4 font-mono text-gray-700">{order.cisloObjednavky}</td>
                                            <td className="p-4">
                                                {(order.billFullName && String(order.billFullName).toLowerCase() !== 'nan') ? order.billFullName : order.billCompany || 'N/A'}
                                            </td>
                                            <td className="p-4">
                                                {order.polozkyProdukty?.map(p => p.nazev).join(', ') || 'N/A'}
                                            </td>
                                            <td className="p-4 text-right font-semibold">{`${(order.totalPriceWithoutVat || 0).toLocaleString('cs-CZ')} CZK`}</td>
                                            <td className="p-4 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={!!order.danumOdsouhlaseno}
                                                    onChange={() => handleCheckboxChange(order.id, !!order.danumOdsouhlaseno)}
                                                    className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                     {totalPages > 1 && (
                        <div className="p-4 flex justify-center items-center space-x-2">
                            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                Předchozí
                            </button>
                             <span className="text-sm text-gray-700">
                                Stránka {currentPage} z {totalPages}
                            </span>
                            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                Následující
                            </button>
                        </div>
                    )}
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
                   <ChartContainer title="Podíl jednotlivých produktů">
                        {stats.productPieData.length > 0 ? (
                        <PieChart>
                            <Pie
                                data={stats.productPieData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                                nameKey="name"
                            >
                                {stats.productPieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                             <Tooltip formatter={(value, name) => [`${value} ks`, name]} />
                             <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '12px', paddingLeft: '20px' }} />
                        </PieChart>
                        ) : <p className="text-center text-gray-500">Nedostatek dat pro zobrazení grafu.</p>}
                    </ChartContainer>
                     <ChartContainer title="Srovnání počtu vzorků">
                        <BarChart data={comparisonChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value) => [`${value} ks`, null]}/>
                            <Legend />
                            <Bar dataKey="Počet vzorků" fill="#0088FE" name="Počet vzorků" />
                        </BarChart>
                    </ChartContainer>
                     <ChartContainer title="Srovnání obratu (bez DPH)">
                        <BarChart data={comparisonChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(value) => new Intl.NumberFormat('cs-CZ').format(value)} />
                            <Tooltip formatter={(value) => [`${new Intl.NumberFormat('cs-CZ').format(value)} CZK`, null]}/>
                            <Legend />
                            <Bar dataKey="Obrat" fill="#00C49F" name="Obrat (CZK)" />
                        </BarChart>
                    </ChartContainer>
                </div>

            </main>
        </div>
    );
}

export default App;

